/**
 * Putting the workspace on screen at phone width.
 *
 * At `max-width: 720px` the shell gives its one column to the sidebar, and
 * `.workspace` becomes a hidden fixed layer that only `.mobile-reader-open`
 * brings forward (`styles.css`). On a laptop the class means nothing — the
 * workspace has a column of its own — so this is the whole of the phone's
 * "am I looking at the list, or at a thing from it" state.
 *
 * It lives here rather than at each navigation site because the class and the
 * address bar have to agree. When they drifted, tapping Alerts on a phone
 * loaded the alerts, changed the URL, and left the owner looking at an
 * unchanged note list with no way to see what they had asked for.
 */
import { fillsWorkspace, type Destination } from "./destinations.js";

/** What a destination asks of the phone's workspace layer. */
export type MobileWorkspaceIntent = "open" | "closed" | "keep";

/**
 * Whether arriving at this destination should bring the workspace forward.
 *
 * `keep` is the important case. Search and the account panel are `<dialog>`
 * elements shown over whatever is behind them, and they do not replace it — so
 * opening search while reading a note must leave the note where it was, or
 * closing the dialog would drop the reader back to the list.
 */
export function mobileWorkspaceIntent(destination: Destination): MobileWorkspaceIntent {
  if (destination.kind === "search" || destination.kind === "account") return "keep";
  return fillsWorkspace(destination) ? "open" : "closed";
}

/**
 * Brings the workspace forward, or puts it away, to match the destination.
 *
 * Takes the element so a test can drive it against a mounted shell; in the app
 * it is always `document.body`, which is what the stylesheet selects on.
 */
export function applyMobileWorkspace(body: Element, destination: Destination): void {
  const intent = mobileWorkspaceIntent(destination);
  if (intent === "keep") return;
  body.classList.toggle("mobile-reader-open", intent === "open");
}
